import type { PortalTypeKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import {
  Banknote,
  Briefcase,
  CalendarClock,
  FileCheck,
  FileText,
  Lightbulb,
  MoreHorizontal,
  Palmtree,
  Plane,
  Receipt,
  Settings,
  Sparkles,
  UserPlus,
  Wallet,
  type LucideIcon,
} from 'lucide-react'

export type PortalTypeCatalogEntry = {
  icon: LucideIcon
  /** Título exibido no catálogo (se diferente do label da API). */
  catalogTitle?: string
  description: string
}

/** Tipos ocultos no portal PJ (não aparecem no Conecta PJ original). */
export const PORTAL_PJ_HIDDEN_TYPES = new Set<string>(['G0_GESTAO_GERAL'])

const OUTROS_PORTAL_ORDER = ['O1_OUTROS_COMERCIAL'] as const

const GESTAO_PORTAL_ORDER = [
  'G1_ENTRADA_CONTRATO',
  'G2_ATUALIZACAO',
  'G3_2_VIA',
  'G4_ENCERRAMENTO',
] as const

const PJ_PORTAL_ORDER = [
  'P1_ENTRADA_CONTRATO',
  'P2_ATUALIZACAO_CONTRATO',
  'P3_ENTRADA_PARCEIRO',
  'P4_AREA_COMERCIAL',
  'P5_ACESSO',
  'P6_NOVA',
  'P7_SERVICOS_CLIENTE',
  'P8_ENCERRAMENTO',
  'P9_2_VIA',
] as const

export function isPortalPjCatalogType(key: string): boolean {
  return !PORTAL_PJ_HIDDEN_TYPES.has(key)
}

export function sortPortalCatalogTypes<T extends { key: string }>(
  items: T[],
  area?: string,
): T[] {
  const order =
    area === 'GESTAO'
      ? GESTAO_PORTAL_ORDER
      : area === 'PJ'
        ? PJ_PORTAL_ORDER
        : area === 'OUTROS'
          ? OUTROS_PORTAL_ORDER
          : null
  if (!order) return items
  const rank = new Map<string, number>(order.map((k, i) => [k, i]))
  return [...items].sort(
    (a, b) => (rank.get(a.key) ?? 999) - (rank.get(b.key) ?? 999),
  )
}

/** Textos do catálogo do portal Rocket. */
export const PORTAL_TYPE_CATALOG: Partial<Record<PortalTypeKey | string, PortalTypeCatalogEntry>> = {
  P1_ENTRADA_CONTRATO: {
    icon: Plane,
    description:
      'Solicite viagens a trabalho informando destino, datas, motivo e estimativa de custos.',
  },
  P2_ATUALIZACAO_CONTRATO: {
    icon: FileText,
    description:
      'Atualização de contrato de prestação de serviço (PJ). Prazo mínimo de análise: 24 horas.',
  },
  P3_ENTRADA_PARCEIRO: {
    icon: Palmtree,
    catalogTitle: 'Descanso contratual acordado (PJ)',
    description:
      'Formalize uma pausa nas atividades prestadas. Exige 12 meses de parceria, 12 NFs emitidas e aprovação da liderança focal.',
  },
  P4_AREA_COMERCIAL: {
    icon: Briefcase,
    catalogTitle: 'Serviços complementares (Zenklub)',
    description:
      'Ative acesso opcional a psicólogo ou nutricionista. Benefício de apoio, sem vínculo empregatício. SLA mínimo de 14h.',
  },
  P5_ACESSO: {
    icon: Wallet,
    description:
      'Fechou o freela? Hora de receber! Alinhe CNPJ e dados bancários antes de abrir o chamado.',
  },
  P6_NOVA: {
    icon: CalendarClock,
    catalogTitle: 'Solicitação de Ausência',
    description:
      'Registro de ausência operacional temporária (licença ou indisponibilidade). Informe período e motivo.',
  },
  P7_SERVICOS_CLIENTE: {
    icon: Receipt,
    description: 'Solicitação de reembolso. Lembre-se de anexar a nota fiscal (NF-e) do gasto.',
  },
  P8_ENCERRAMENTO: {
    icon: Banknote,
    description: 'Adiantamento contratual (PJ). Análise em até 16 horas úteis após envio completo.',
  },
  P9_2_VIA: {
    icon: Lightbulb,
    description: 'Tem uma ideia? Conta pra gente! Sugestões e melhorias para o ecossistema Rocket.',
  },
  G1_ENTRADA_CONTRATO: {
    icon: Plane,
    catalogTitle: 'Solicitação de viagem de negócios',
    description:
      'Agende e organize viagens a trabalho. Informe destino, datas, motivo e necessidades de hospedagem ou passagens.',
  },
  G2_ATUALIZACAO: {
    icon: FileCheck,
    catalogTitle: 'Encerramento de contrato de prestação de serviço PJ',
    description:
      'Formalize o encerramento do contrato de prestação de serviços. SLA mínimo de 10 horas após o envio completo.',
  },
  G3_2_VIA: {
    icon: UserPlus,
    catalogTitle: 'Solicitação de acesso',
    description:
      'Peça novos acessos a sistemas ou regularize bloqueios e permissões. SLA de 6 horas.',
  },
  G4_ENCERRAMENTO: {
    icon: Settings,
    catalogTitle: 'Criar nova automação de sistemas (todos)',
    description:
      'Solicite automações e integrações entre ferramentas internas. SLA de 48 horas.',
  },
  G5_CONTRATACAO_PJ: {
    icon: UserPlus,
    catalogTitle: 'Contratação de profissional PJ',
    description:
      'Gestores: solicite a entrada de um novo profissional PJ no setor com dados do candidato e aprovação da liderança.',
  },
  C0_NOVO_CLIENTE: {
    icon: Sparkles,
    description: 'Entrada de contratos — adição de produtos personalizados oferecidos pela agência.',
  },
  C1_ATUALIZACAO_CADASTRO: {
    icon: FileText,
    description: 'Atualização de cadastro ou dados comerciais de cliente existente.',
  },
  C2_SEGUNDA_VIA: {
    icon: FileText,
    description: 'Atualização de contrato comercial já vigente com o cliente.',
  },
  C3_NOTA_FISCAL: {
    icon: Receipt,
    description: 'Solicitação de 2ª via de nota fiscal. SLA de 12 horas.',
  },
  C4_CONTRATO_AGRADA: {
    icon: FileCheck,
    description: 'Contrato comercial de cliente via RocketSearch. SLA de 16 horas.',
  },
  C5_ENCERRAMENTO: {
    icon: FileText,
    description: 'Encerramento de contrato comercial com cliente.',
  },
  C6_SERVICOS_CLIENTE: {
    icon: Briefcase,
    description: 'Serviço específico avulso para cliente. SLA de 16 horas.',
  },
  S1_PROCESSOS: {
    icon: Settings,
    description: 'Demandas de processos internos e melhoria contínua.',
  },
  S2_ENCERRAMENTO: {
    icon: FileText,
    description: 'Encerramento de demanda na área de processos.',
  },
  S3_2_VIA: {
    icon: Receipt,
    description: '2ª via de documento na área de processos.',
  },
  S4_SOFTWARE: {
    icon: Settings,
    description: 'Solicitações de software, integrações e ferramentas.',
  },
  S5_SUPORTE: {
    icon: Sparkles,
    description: 'Suporte técnico e dúvidas sobre sistemas.',
  },
  O1_OUTROS_COMERCIAL: {
    icon: MoreHorizontal,
    catalogTitle: 'Outros — demanda comercial',
    description:
      'Use quando a solicitação for comercial e não se encaixar nas categorias de clientes, contratos ou notas fiscais.',
  },
}

export function getPortalTypeCatalog(key: string): PortalTypeCatalogEntry {
  return (
    PORTAL_TYPE_CATALOG[key] ?? {
      icon: FileText,
      description: `Prazo de atendimento (SLA): consulte o time financeiro após abertura.`,
    }
  )
}
